import { uuid } from "@sanity/uuid";
import classNames from "classnames";
import { Form, Formik, FormikProps, FormikValues } from "formik";
import React, { useContext, useRef, useState } from "react";
import Recaptcha from "react-recaptcha";
import siteConfig from "../../config/siteConfig";
import { PageContext } from "../../context/PageContext";
import useTranslations from "../../hooks/useTranslations";
import { SanityFormField } from "../../models/form/SanityFormField";
import {
  SanityFormInputField,
  SanityInputFileUploadProps
} from "../../models/form/SanityFormInputField";
import { SanityFormProps } from "../../models/form/SanityFormProps";
import { SanityInputFieldType } from "../../models/form/SanityInputFieldType";
import { isNullOrWhiteSpace } from "../../shared/string";
import {
  getUtmParametersString,
  gtmDispatchEvent,
  ValidGtmEventData
} from "../../utilities/gtm";
import { isBrowser } from "../../utilities/isBrowser";
import Button, { ButtonSize } from "../Buttons/Button";
import FormikRecaptcha from "./FormikRecaptcha";
import { Inputs } from "./Inputs";
import HoneypotField from "./Inputs/HoneypotField";
import SubmitButton from "./SubmitButton";

const HONEYPOT_FIELD_NAME = "hp-field";
const UTM_PARAMETERS_FIELD_NAME = "utm_parameters";
const HIDDEN_FIELDS_FIELD_NAME = "hidden_fields";
// Note: honeypot and recaptcha can be used together on Netlify,
// but here the useFormHoneypot flag is used to toggle between them.
const USE_HONEYPOT = siteConfig.useFormHoneypot;
const USE_RECAPTCHA = !isNullOrWhiteSpace(siteConfig.reCaptchaSiteKey) && !USE_HONEYPOT;

const getFormikInitialValues = (inputs: SanityFormField[]) => {
  const initialValues = inputs
    .filter(
      (input): input is SanityFormInputField =>
        input._type !== SanityInputFieldType.FormHeading
    )
    .reduce(
      (acc, input) => ({
        ...acc,
        // Fields with the same name but different types require an alias in graphql
        [input.slug.current]: input.defaultValue ?? input.defaultValueBool ?? ""
      }),
      {}
    );

  // Formik requires ALL fields to have an initialValue for some reason, otherwise stuff stops working
  // (specifically "set all fields as touched on submit", see https://github.com/formium/formik/issues/691)
  if (USE_RECAPTCHA) {
    initialValues["g-recaptcha-response"] = null;
  }

  if (USE_HONEYPOT) {
    // Must not be null or the form submission is ignored
    initialValues[HONEYPOT_FIELD_NAME] = "";
  }

  return initialValues;
};

const encode = data => {
  const formData = new FormData();

  for (const key of Object.keys(data)) {
    formData.append(key, data[key]);
  }

  return formData;
};

const processFileUpload = async (file: File, formSlug: string) => {
  if (!file) {
    return null;
  }

  const formData = new FormData();
  formData.append("file", file);
  formData.append("d", siteConfig.sanityDataset ?? "invalid_site"); // Dataset happens to be a proper name for this usage
  formData.append("formName", formSlug);

  try {
    const res = await fetch("https://anti-forms.azurewebsites.net/api/UploadFile", {
      method: "POST",
      body: formData
    });

    const { path } = await res.json();

    return encodeURI(path);
  } catch (err) {
    console.error(err);
    return null;
  }
};

// Get all file inputs, including nested inputs.
const getFileInputs = (inputs: SanityFormField[]): SanityInputFileUploadProps[] => {
  const fileInputs: SanityInputFileUploadProps[] = [];

  for (const input of inputs) {
    switch (input._type) {
      case SanityInputFieldType.FileUpload:
        fileInputs.push(input);
        break;
      case SanityInputFieldType.Checkboxes:
      case SanityInputFieldType.Dropdown:
      case SanityInputFieldType.RadioButtons:
        for (const option of input.options) {
          if (option.inputs) {
            fileInputs.push(...getFileInputs(option.inputs));
          }
        }
        break;
    }
  }

  return fileInputs;
};

const SanityForm = ({
  _id,
  _rawInputs,
  gtmFormType = "standardForm",
  gtmFormValueFieldName,
  inputs,
  modifyValue,
  onSubmitted,
  slug,
  submitCompleteText,
  submitText,
  submittingText,
  fileLinkForDownloadAfterSubmission,
  fileLinkDescription,
  customerType,
  variant = "default",
  hiddenValues = []
}: SanityFormProps) => {
  const translations = useTranslations();
  const { rawI18nLang } = useContext(PageContext);
  const [renderRecaptcha, setRenderRecaptcha] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitComplete, setSubmitComplete] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);
  const filteredInputs =
    (inputs || _rawInputs)?.filter(
      input =>
        input?._type === SanityInputFieldType.FormHeading ||
        !isNullOrWhiteSpace(input?.slug?.current)
    ) ?? [];

  const recaptchaRef = useRef<Recaptcha>(null);
  const formikRef = useRef<FormikProps<FormikValues>>(null);

  // Note: onSubmit is attached to Formik component, but triggered first when
  // ReCaptcha has been verified. See FormikRecaptcha.tsx
  const onSubmit = async (values, _actions) => {
    setSubmitting(true);
    setSubmitFailed(false);

    const fileInputs = getFileInputs(filteredInputs);
    let formValues = Object.assign({}, values); // Avoid mutating original "values"

    if (modifyValue) {
      const modifiedValue = modifyValue(formValues);
      if (modifiedValue) {
        formValues = { ...formValues, ...modifiedValue };
      }
    }

    // Process secure file uploads
    for (const fileInput of fileInputs.filter(input => input.secure)) {
      const fileUrl = await processFileUpload(
        values[fileInput.slug.current]?.file,
        slug.current
      );
      formValues[fileInput.slug.current] = fileUrl;
    }

    // Process non-secure file uploads
    for (const fileInput of fileInputs.filter(input => !input.secure)) {
      formValues[fileInput.slug.current] = values[fileInput.slug.current]?.file;
    }

    let success = false;
    const formId = _id;
    const formUrl = `${window.location.origin}${window.location.pathname}`;
    let response: Response | undefined;

    const formSubmissionId = uuid();

    formValues[HIDDEN_FIELDS_FIELD_NAME] = JSON.stringify(
      hiddenValues?.map(hiddenValue => ({
        name: hiddenValue.name,
        value: hiddenValue.value
      }))
    );

    // Add utm_parameters
    formValues[UTM_PARAMETERS_FIELD_NAME] = getUtmParametersString();

    try {
      response = await fetch(window.location.pathname, {
        method: "POST",
        body: encode({
          // fieldNames is used to filter the fields in submission-created, since Netlify always passes all form inputs
          // that exists in the markup to submission-created, no matter if they have a value in the POST body or not.
          fieldNames: Object.keys(formValues)
            .filter(key => key !== HONEYPOT_FIELD_NAME)
            .join(","),
          "form-name": slug.current,
          formId,
          formUrl,
          formSubmissionId,
          sanityDataset: siteConfig.sanityDataset,
          i18nLang: rawI18nLang,
          ...formValues
        })
      });

      success = response.ok;

      if (success && response) {
        const gtmData: ValidGtmEventData = {
          formId: _id,
          formName: slug.current,
          formType: gtmFormType,
          formSubmissionId,
          customerType,
          // TODO - reenable once our JSON is properly returned from Netlify Forms
          // formSubmissionId: jsonResult.formSubmissionId ?? "",
          ...(!isNullOrWhiteSpace(gtmFormValueFieldName)
            ? {
                formValue: formValues[gtmFormValueFieldName] ?? ""
              }
            : {})
        };

        gtmDispatchEvent("formSubmissionSuccess", gtmData);
        onSubmitted?.();
      }
    } catch (err) {
      console.error("Failed to submit form", err);
    }

    setSubmitting(false);
    setSubmitComplete(success);
    setSubmitFailed(!success);
  };

  const executeRecaptcha = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.nativeEvent.preventDefault();

    if (!recaptchaRef?.current) {
      console.error("ReCaptcha not initialized correctly");
      return;
    }

    recaptchaRef.current.reset();
    recaptchaRef.current.execute();
  };

  const handleFormikSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (!formikRef?.current) {
      e.preventDefault();
      e.nativeEvent.preventDefault();
      console.error("Formik form not initialized correctly");
      return;
    }

    formikRef.current.handleSubmit(e);
  };

  return (
    <Formik
      initialValues={getFormikInitialValues(filteredInputs)}
      innerRef={formikRef}
      onSubmit={onSubmit}
    >
      <Form
        data-netlify="true"
        data-netlify-recaptcha={USE_RECAPTCHA ? "true" : undefined}
        data-netlify-honeypot={USE_HONEYPOT ? HONEYPOT_FIELD_NAME : undefined}
        onFocus={() => setRenderRecaptcha(true)}
        onSubmit={USE_RECAPTCHA ? executeRecaptcha : handleFormikSubmit}
        encType="multipart/form-data" // added by Netlify
        method="post" // added by Netlify
        name={slug.current}
      >
        {/* Added by Netlify. React hydration fails if form-name not already here for SSR. */}
        <input type="hidden" name="form-name" value={slug.current} />
        <input type="hidden" name="fieldNames" />
        <input type="hidden" name="formId" />
        <input type="hidden" name="formUrl" />
        <input type="hidden" name="formSubmissionId" />
        <input type="hidden" name="sanityDataset" />
        <input type="hidden" name="i18nLang" />
        <input type="hidden" name={UTM_PARAMETERS_FIELD_NAME} />
        <input type="hidden" name={HIDDEN_FIELDS_FIELD_NAME} />

        <Inputs
          inputs={filteredInputs}
          inputContainerVariant={variant === "default" ? "default" : "sectioned"}
          readonly={submitting || submitComplete}
        />

        {renderRecaptcha && USE_RECAPTCHA && isBrowser() && (
          <FormikRecaptcha id={`${slug.current}-recaptcha`} ref={recaptchaRef} />
        )}

        {USE_HONEYPOT && <HoneypotField name={HONEYPOT_FIELD_NAME} />}

        <div
          className={classNames("flex flex-wrap gap-4", {
            "justify-center": variant !== "calculation-form",
            "mt-8 lg:mt-10": variant === "modal"
          })}
        >
          <SubmitButton
            className={classNames(variant === "modal" ? "max-w-full" : "mt-6 lg:mt-10")}
            color={variant === "default" ? "primary" : "primary-light"}
            size={variant === "modal" ? ButtonSize.Small : ButtonSize.Normal}
            submitting={submitting}
            submitComplete={submitComplete}
            submitText={submitText}
            submittingText={submittingText}
            submitCompleteText={submitCompleteText}
            widthClassName={variant === "modal" ? "w-72" : undefined}
          />

          {variant === "modal" && (
            <Button
              className="w-72 max-w-full"
              type="button"
              color="primary-inverted"
              size={ButtonSize.Small}
              onClick={() => onSubmitted?.()}
            >
              {translations.close ?? "Close"}
            </Button>
          )}
        </div>

        <div className="flex flex-col items-center">
          {submitFailed && (
            <p
              className={classNames("text-red-600 my-6", {
                "w-full": variant === "calculation-form"
              })}
            >
              {translations.error ?? "Error"}
            </p>
          )}

          {submitComplete && fileLinkDescription && (
            <p className="text-md mt-10 lg:mt-14 mb-0 text-center">
              {fileLinkDescription}
            </p>
          )}

          {submitComplete && fileLinkForDownloadAfterSubmission && (
            <Button
              className="mt-5 lg:mt-7"
              color="secondary"
              size={ButtonSize.Small}
              type="link"
              isExternalLink={true}
              url={`${fileLinkForDownloadAfterSubmission.file.asset.url}?dl=${fileLinkForDownloadAfterSubmission.file.asset.originalFilename}`}
            >
              {fileLinkForDownloadAfterSubmission.title}
            </Button>
          )}
        </div>
      </Form>
    </Formik>
  );
};

export default SanityForm;
